<div class="staff-lead-detail core-screen">
  @if (loading()) {
    <p class="staff-lead-detail__state">{{ 'common.loading' | translate }}</p>
  } @else if (errorKind()) {
    <div class="staff-lead-detail__empty">
      <h2 class="staff-lead-detail__empty-title">
        {{
          (errorKind() === 'notFound'
            ? 'STAFF.LEADS.DETAIL.NOT_FOUND_TITLE'
            : 'STAFF.LEADS.DETAIL.LOAD_ERROR_TITLE'
          ) | translate
        }}
      </h2>
      <p class="staff-lead-detail__empty-hint">
        {{
          (errorKind() === 'notFound'
            ? 'STAFF.LEADS.DETAIL.NOT_FOUND_HINT'
            : 'STAFF.LEADS.DETAIL.LOAD_ERROR_HINT'
          ) | translate
        }}
      </p>
    </div>
  } @else if (lead(); as item) {
    <header class="staff-lead-detail__header">
      <div class="staff-lead-detail__badges">
        <span class="badge badge--id">{{ formatStaffLeadShortId(item.id) }}</span>
        <span class="badge" [attr.data-status]="item.status">{{
          statusLabelKey(item.status) | translate
        }}</span>
        <span class="badge">{{ kindLabelKey(item.kind) | translate }}</span>
      </div>
      <h1 class="staff-lead-detail__title">{{ item.title }}</h1>
      <p class="staff-lead-detail__meta">
        {{ 'STAFF.LEADS.DETAIL.CREATED_AT' | translate }}:
        {{ item.created_at | date: 'd MMMM y, HH:mm' }}
      </p>
    </header>

    <section class="staff-lead-detail__section">
      <h2 class="staff-lead-detail__section-title">
        {{ 'STAFF.LEADS.DETAIL.CONTACT' | translate }}
      </h2>
      @if (item.guest_name?.trim(); as guestName) {
        <p class="staff-lead-detail__row">
          <span class="label">{{ 'STAFF.LEADS.DETAIL.NAME' | translate }}</span>
          <span class="value">{{ guestName }}</span>
        </p>
      }
      <p class="staff-lead-detail__row">
        <span class="label">{{ 'STAFF.LEADS.DETAIL.PHONE' | translate }}</span>
        <span class="value">{{ formatLeadPhone(item.phone) }}</span>
      </p>
      @if (hasLinkedUser(item)) {
        <p class="staff-lead-detail__row">
          <span class="label">{{ profileLabelKey(item) | translate }}</span>
          @if (userLink(item); as link) {
            <a class="value link" [routerLink]="link">{{ item.user_id }}</a>
          }
        </p>
      } @else if (isForeignRegistrationWithoutUser(item)) {
        <p class="staff-lead-detail__row">
          <span class="label">{{ 'STAFF.LEADS.DETAIL.CLIENT' | translate }}</span>
          <span class="value">{{ 'STAFF.LEADS.DETAIL.NO_CLIENT' | translate }}</span>
        </p>
        <a
          class="staff-lead-detail__create-client link"
          [routerLink]="createClientLink()"
          [queryParams]="createClientQueryParams(item)"
        >
          {{ 'STAFF.LEADS.DETAIL.CREATE_CLIENT' | translate }}
        </a>
      }
    </section>

    @if (item.message?.trim()) {
      <section class="staff-lead-detail__section">
        <h2 class="staff-lead-detail__section-title">
          {{ 'STAFF.LEADS.DETAIL.MESSAGE' | translate }}
        </h2>
        @if (calculatorDisplay(item); as calc) {
          <div class="staff-lead-detail__message-body">
            @if (calc.pageLine) {
              <p class="staff-lead-detail__message-line">{{ calc.pageLine }}</p>
            }
            @if (calc.serviceLine) {
              <p class="staff-lead-detail__message-line">{{ calc.serviceLine }}</p>
            }
            @if (calc.calculatorSectionTitle) {
              <p class="staff-lead-detail__message-subtitle">
                {{ calc.calculatorSectionTitle }}
              </p>
            }
            @if (calc.sectionHeader) {
              <p
                class="staff-lead-detail__message-line staff-lead-detail__message-line--muted"
              >
                {{ calc.sectionHeader }}
              </p>
            }
            @if (calc.itemLines.length) {
              <ul class="staff-lead-detail__message-items">
                @for (line of calc.itemLines; track line) {
                  <li>{{ line }}</li>
                }
              </ul>
            }
            @if (calc.summaryLines.length) {
              <ul class="staff-lead-detail__message-summary">
                @for (line of calc.summaryLines; track line) {
                  <li>{{ line }}</li>
                }
              </ul>
            }
          </div>
        } @else {
          <p class="staff-lead-detail__text">{{ item.message }}</p>
        }
      </section>
    }

    @if (item.context_lines?.length && !item.message?.trim()) {
      <section class="staff-lead-detail__section">
        <h2 class="staff-lead-detail__section-title">
          {{ 'STAFF.LEADS.DETAIL.CONTEXT' | translate }}
        </h2>
        <ul class="staff-lead-detail__context">
          @for (line of item.context_lines; track $index) {
            <li>{{ line }}</li>
          }
        </ul>
      </section>
    }

    @if (
      (item.page_path || item.service_type || item.service_id) &&
      !item.message?.trim()
    ) {
      <section class="staff-lead-detail__section">
        <h2 class="staff-lead-detail__section-title">
          {{ 'STAFF.LEADS.DETAIL.SOURCE' | translate }}
        </h2>
        @if (item.page_path) {
          <p class="staff-lead-detail__row">
            <span class="label">{{ 'STAFF.LEADS.DETAIL.PAGE' | translate }}</span>
            <span class="value">{{ item.page_path }}</span>
          </p>
        }
        @if (item.service_type || item.service_id) {
          <p class="staff-lead-detail__row">
            <span class="label">{{ 'STAFF.LEADS.DETAIL.SERVICE' | translate }}</span>
            <span class="value"
              >{{ item.service_type || '—' }} / {{ item.service_id || '—' }}</span
            >
          </p>
        }
      </section>
    }

    @if (item.rating != null || item.promocode || item.discount_percent != null) {
      <section class="staff-lead-detail__section">
        <h2 class="staff-lead-detail__section-title">
          {{ 'STAFF.LEADS.DETAIL.EXTRA' | translate }}
        </h2>
        @if (item.rating != null) {
          <p class="staff-lead-detail__row">
            <span class="label">{{ 'STAFF.LEADS.DETAIL.RATING' | translate }}</span>
            <span class="value">{{ item.rating }}</span>
          </p>
        }
        @if (item.promocode) {
          <p class="staff-lead-detail__row">
            <span class="label">{{ 'STAFF.LEADS.DETAIL.PROMOCODE' | translate }}</span>
            <span class="value">{{ item.promocode }}</span>
          </p>
        }
        @if (item.discount_percent != null) {
          <p class="staff-lead-detail__row">
            <span class="label">{{
              'STAFF.LEADS.DETAIL.DISCOUNT_PERCENT' | translate
            }}</span>
            <span class="value">{{ item.discount_percent }}%</span>
          </p>
        }
      </section>
    }

    <section class="staff-lead-detail__section">
      <h2 class="staff-lead-detail__section-title">
        {{ 'STAFF.LEADS.DETAIL.WORKFLOW' | translate }}
      </h2>
      <label class="staff-lead-detail__field">
        <span class="label">{{ 'STAFF.LEADS.DETAIL.STATUS' | translate }}</span>
        <select
          class="staff-lead-detail__select"
          [ngModel]="selectedStatus()"
          (ngModelChange)="selectedStatus.set($event)"
        >
          @for (status of statusOptions; track status) {
            <option [ngValue]="status">
              {{ statusLabelKey(status) | translate }}
            </option>
          }
        </select>
      </label>
      <label class="staff-lead-detail__field">
        <span class="label">{{ 'STAFF.LEADS.DETAIL.STAFF_NOTE' | translate }}</span>
        <textarea
          class="staff-lead-detail__textarea"
          rows="4"
          [ngModel]="staffNote()"
          (ngModelChange)="staffNote.set($event)"
          [attr.placeholder]="'STAFF.LEADS.DETAIL.STAFF_NOTE_PLACEHOLDER' | translate"
        ></textarea>
      </label>
      <lib-not-button
        [text]="'STAFF.LEADS.DETAIL.SAVE' | translate"
        [color]="'#140342'"
        [bgColor]="'#E8F9F7'"
        [width]="'100%'"
        [size]="'big'"
        [action]="{ type: 'action' }"
        [disabled]="saving()"
        (actionEvent)="saveChanges()"
      />
    </section>
  }
</div>
